import React, { useState, useEffect } from 'react';

const EMPTY = {
  first_name: '',
  last_name: '',
  is_black_youth: false,
  was_unemployed_at_registration: false,
  start_date: '',
  planned_end_date: '',
  actual_end_date: '',
  is_new_position: false,
  host_type: 'internal',
  host_employer_name: '',
  absorption_status: 'in_progress',
  absorption_date: '',
  notes: '',
};

// start_date + 1 calendar year, as 'YYYY-MM-DD' (matches the backend's own +12-month default).
const addOneYear = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

// Add/edit form for a single Y.E.S youth placement. Rendered inside <Modal> by Yes4Youth.jsx.
// `placement` is null for "add", or an existing placement doc for "edit".
const YesPlacementForm = ({ placement, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(placement ? { ...EMPTY, ...placement } : EMPTY);
    setError('');
  }, [placement]);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Changing start_date auto-fills planned_end_date to +1 year, so it's visible before saving
  // rather than only defaulting silently on the backend. Still editable afterward.
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, start_date: value, planned_end_date: addOneYear(value) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.start_date) {
      setError('Start date is required.');
      return;
    }
    setError('');
    onSave(form);
  };

  return (
    <form className="yes-form" onSubmit={handleSubmit}>
      {error && <div className="yes-form-error">{error}</div>}

      <div className="yes-form-row">
        <label>
          First Name
          <input value={form.first_name} onChange={set('first_name')} />
        </label>
        <label>
          Last Name
          <input value={form.last_name} onChange={set('last_name')} />
        </label>
      </div>

      <div className="yes-form-row">
        <label>
          Start Date
          <input type="date" value={form.start_date} onChange={handleStartDateChange} required />
        </label>
        <label>
          Planned End Date
          <input type="date" value={form.planned_end_date} onChange={set('planned_end_date')} />
          <span className="yes-form-hint">Auto-calculated as 1 year from start date — adjust if needed</span>
        </label>
      </div>

      <div className="yes-form-row">
        <label>
          Actual End Date
          <input type="date" value={form.actual_end_date || ''} onChange={set('actual_end_date')} />
        </label>
        <label>
          Absorption Status
          <select value={form.absorption_status} onChange={set('absorption_status')}>
            <option value="in_progress">In Progress</option>
            <option value="absorbed">Absorbed</option>
            <option value="not_absorbed">Not Absorbed</option>
            <option value="exited_early">Exited Early</option>
          </select>
        </label>
      </div>

      {form.absorption_status === 'absorbed' && (
        <div className="yes-form-row">
          <label>
            Absorption Date
            <input type="date" value={form.absorption_date || ''} onChange={set('absorption_date')} />
          </label>
        </div>
      )}

      <div className="yes-form-row">
        <label>
          Host Type
          <select value={form.host_type} onChange={set('host_type')}>
            <option value="internal">Internal</option>
            <option value="sponsored_eme_qse">Sponsored EME/QSE</option>
          </select>
        </label>
        <label>
          Host Employer Name
          <input value={form.host_employer_name} onChange={set('host_employer_name')} />
        </label>
      </div>

      <div className="yes-form-row yes-form-row--checks">
        <label className="yes-form-check">
          <input type="checkbox" checked={!!form.is_black_youth} onChange={set('is_black_youth')} />
          Black Youth
        </label>
        <label className="yes-form-check">
          <input type="checkbox" checked={!!form.was_unemployed_at_registration} onChange={set('was_unemployed_at_registration')} />
          Unemployed at Registration
        </label>
        <label className="yes-form-check">
          <input type="checkbox" checked={!!form.is_new_position} onChange={set('is_new_position')} />
          New Position
        </label>
      </div>

      <label className="yes-form-notes">
        Notes
        <textarea value={form.notes} onChange={set('notes')} rows={3} />
      </label>

      <div className="yes-form-actions">
        <button type="button" className="yes-btn yes-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="yes-btn yes-btn--primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Placement'}
        </button>
      </div>
    </form>
  );
};

export default YesPlacementForm;
